import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  retireViewImagesRequests,
  serializeViewImagesPersistence,
  updateViewImageListByID,
} from "actions/view-images-action";
import { createBulkUploadStatusAdapter } from "./bulkUploadStatusAdapter";
import { getBulkEventTuple, getBulkRowTuples } from "./bulkUploadProjection";
import { useMlStatusRepository } from "./mlStatusContext";
import { persistViewImagesStatusProjection } from "./viewImagesPersistence";
import {
  getViewImagesRetryableRows,
  isViewImagesKnownStatus,
  projectViewImagesStatusEvent,
} from "./viewImagesProjection";
import { resyncViewImagesStatuses } from "./viewImagesSnapshot";

const nonemptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const activeScope = (descriptor, rows, primaryOrgId) => {
  const hasReviewOrgId =
    descriptor?.reviewOrgId !== null && descriptor?.reviewOrgId !== undefined;
  const hasExactOrganizationScope =
    descriptor?.orgId === descriptor?.primaryOrgId
      ? !hasReviewOrgId
      : hasReviewOrgId && descriptor?.reviewOrgId === descriptor?.orgId;
  if (
    !descriptor ||
    !Number.isSafeInteger(descriptor.generation) ||
    !Number.isSafeInteger(descriptor.orgId) ||
    !Number.isSafeInteger(descriptor.primaryOrgId) ||
    descriptor.primaryOrgId !== Number(primaryOrgId) ||
    !hasExactOrganizationScope ||
    !Array.isArray(descriptor.activeTuples) ||
    descriptor.activeTuples.length === 0 ||
    new Set(descriptor.activeTuples).size !== descriptor.activeTuples.length
  ) {
    return null;
  }
  const eligibleRows = rows.filter(
    (row) =>
      row?.mrn === null &&
      Number(row?.orgz_id) === descriptor.orgId &&
      Array.isArray(row?.status) &&
      row.status.length > 0 &&
      getBulkRowTuples(row).length === row.status.length
  );
  const tuples = eligibleRows.flatMap(getBulkRowTuples).sort();
  const expected = [...descriptor.activeTuples].sort();
  if (
    tuples.length !== expected.length ||
    tuples.some((tuple, index) => tuple !== expected[index])
  ) {
    return null;
  }
  if (getViewImagesRetryableRows(eligibleRows, descriptor.orgId).length === 0) {
    return null;
  }
  return { eligibleRows, tuples };
};

const retryableTuples = (rows, orgId) =>
  new Set(getViewImagesRetryableRows(rows, orgId).flatMap(getBulkRowTuples));

export const ViewImagesRealtimeOwner = ({
  adapterFactory = createBulkUploadStatusAdapter,
  authToken,
  descriptor,
  location,
  primaryOrgId,
  publish,
  resync,
  retire,
  rows = [],
}) => {
  const repository = useMlStatusRepository();
  const authTokenRef = useRef(authToken);
  const authChanged = authTokenRef.current !== authToken;
  const scope = authChanged
    ? null
    : activeScope(descriptor, rows, primaryOrgId);
  const canTrack =
    location?.pathname === "/view-images" &&
    nonemptyString(authToken) &&
    typeof publish === "function" &&
    typeof resync === "function" &&
    scope;
  const scopeKey = canTrack
    ? `view-images:${descriptor.orgId}:${
        descriptor.generation
      }:${scope.tuples.join(",")}`
    : null;
  const rowsRef = useRef(rows);
  const publishRef = useRef(publish);
  const resyncRef = useRef(resync);
  const adapterFactoryRef = useRef(adapterFactory);
  rowsRef.current = rows;
  publishRef.current = publish;
  resyncRef.current = resync;
  adapterFactoryRef.current = adapterFactory;

  useEffect(() => {
    if (authTokenRef.current !== authToken && typeof retire === "function") {
      retire();
    }
    authTokenRef.current = authToken;
  }, [authToken, retire]);

  useEffect(() => {
    if (!repository || !scopeKey) return undefined;
    let eventGeneration = 0;
    let snapshotGeneration = 0;
    const acceptedTuples = new Set(scope.tuples);
    const persist = (options) =>
      serializeViewImagesPersistence(
        () => persistViewImagesStatusProjection(options),
        options.isCurrent
      );
    const project = (currentRows, event) =>
      projectViewImagesStatusEvent(currentRows, event, {
        orgId: descriptor.orgId,
      });
    const adapter = adapterFactoryRef.current({
      project,
      persist,
      publish: (row) => publishRef.current(row, { realtime: true }),
    });
    const runSnapshot = (context) => {
      const acceptedEventGeneration = eventGeneration;
      const acceptedSnapshotGeneration = ++snapshotGeneration;
      const snapshotContext = {
        ...context,
        isCurrent: () =>
          context.isCurrent() &&
          eventGeneration === acceptedEventGeneration &&
          snapshotGeneration === acceptedSnapshotGeneration,
      };
      return resyncRef.current({
        authToken,
        orgId: descriptor.orgId,
        primaryOrgId: descriptor.primaryOrgId,
        reviewOrgId: descriptor.reviewOrgId,
        rows: rowsRef.current,
        signal: snapshotContext.signal,
        isCurrent: snapshotContext.isCurrent,
        persist,
      });
    };
    const unsubscribe = repository.subscribe({
      scopeKey,
      accepts: (event) => {
        const tuple = getBulkEventTuple(event);
        return (
          isViewImagesKnownStatus(event?.status) &&
          acceptedTuples.has(tuple) &&
          retryableTuples(rowsRef.current, descriptor.orgId).has(tuple)
        );
      },
      project: (event, context) => {
        eventGeneration += 1;
        return adapter.apply({
          getRows: () => rowsRef.current,
          event,
          isCurrent: context.isCurrent,
        });
      },
      resync: runSnapshot,
    });
    return () => {
      unsubscribe();
      adapter.cleanup();
    };
  }, [repository, scopeKey]);

  return null;
};

const mapStateToProps = (store) => ({
  descriptor: store.viewImagesStore?.realtimeDescriptor || null,
  primaryOrgId: store.userStore?.userProfile?.orgz_id,
  rows: store.viewImagesStore?.viewImagesList || [],
});

export default connect(mapStateToProps, {
  publish: updateViewImageListByID,
  resync: resyncViewImagesStatuses,
  retire: retireViewImagesRequests,
})(withRouter(ViewImagesRealtimeOwner));
